import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';

const API = (path) =>
  fetch(path, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

export default function OwnerCommissionAudit() {
  const [commissions, setCommissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState('');
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState('total_allocated');

  useEffect(() => {
    API('/api/owner/commission-audit')
      .then(r => r.json())
      .then(d => { setCommissions(d.commissions || []); setLoading(false); })
      .catch(() => { setError('Failed to load'); setLoading(false); });
  }, []);

  const filtered = commissions
    .filter(c =>
      !search ||
      c.introducer_name.toLowerCase().includes(search.toLowerCase()) ||
      c.company_name.toLowerCase().includes(search.toLowerCase())
    )
    .sort((a, b) => parseFloat(b[sortKey] || 0) - parseFloat(a[sortKey] || 0));

  const totals = filtered.reduce((acc, c) => ({
    allocated: acc.allocated + parseFloat(c.total_allocated || 0),
    paid:      acc.paid      + parseFloat(c.total_paid      || 0),
    unpaid:    acc.unpaid    + parseFloat(c.total_unpaid    || 0),
  }), { allocated: 0, paid: 0, unpaid: 0 });

  const fmtMoney = v => `£${parseFloat(v || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const sortBtn = (key, label) => (
    <button onClick={() => setSortKey(key)} style={{ ...thBtn, fontWeight: sortKey === key ? 700 : 400 }}>{label}{sortKey === key ? ' ▾' : ''}</button>
  );

  return (
    <div className="dashboard-container">
      <Sidebar />
      <main className="dashboard-main">
        <h1 style={{ fontSize: '24px', fontWeight: 700, marginBottom: '4px' }}>Commission Audit</h1>
        <p style={{ color: '#6b7280', marginBottom: '24px' }}>Platform-wide introducer commission structures and payments</p>

        {error && <div style={alertRed}>{error}</div>}

        {/* Summary cards */}
        <div style={{ display: 'flex', gap: '16px', marginBottom: '24px' }}>
          {[
            { label: 'Total Allocated', value: fmtMoney(totals.allocated), color: '#3b82f6' },
            { label: 'Total Paid',      value: fmtMoney(totals.paid),      color: '#10b981' },
            { label: 'Outstanding',     value: fmtMoney(totals.unpaid),    color: '#f59e0b' },
          ].map(s => (
            <div key={s.label} style={{ background: '#fff', borderRadius: '8px', padding: '16px 24px', borderLeft: `4px solid ${s.color}`, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', flex: 1 }}>
              <div style={{ fontSize: '22px', fontWeight: 700, color: s.color }}>{s.value}</div>
              <div style={{ fontSize: '13px', color: '#6b7280' }}>{s.label}</div>
            </div>
          ))}
        </div>

        <div style={{ marginBottom: '16px' }}>
          <input
            placeholder="Search introducer or company…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', width: '300px' }}
          />
        </div>

        <div style={{ background: '#fff', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
          {loading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>Loading…</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f9fafb' }}>
                  <th style={th}>Introducer</th>
                  <th style={th}>Company</th>
                  <th style={th}>Type</th>
                  <th style={th}>Fee Structure</th>
                  <th style={th}>{sortBtn('total_deals', 'Deals')}</th>
                  <th style={th}>{sortBtn('total_allocated', 'Allocated')}</th>
                  <th style={th}>{sortBtn('total_paid', 'Paid')}</th>
                  <th style={th}>{sortBtn('total_unpaid', 'Outstanding')}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={8} style={{ padding: '40px', textAlign: 'center', color: '#9ca3af' }}>No data</td></tr>
                ) : filtered.map(c => (
                  <tr key={c.introducer_id} style={{ borderTop: '1px solid #f3f4f6' }}>
                    <td style={td}>
                      <div style={{ fontWeight: 600 }}>{c.introducer_name}</div>
                      <span style={{ ...statusDot, background: c.introducer_status === 'active' ? '#10b981' : '#ef4444' }} />
                      <span style={{ fontSize: '12px', color: '#9ca3af' }}>{c.introducer_status}</span>
                    </td>
                    <td style={td}>{c.company_name}</td>
                    <td style={td}><span style={{ textTransform: 'capitalize', fontSize: '13px' }}>{c.introducer_type}</span></td>
                    <td style={td}>
                      {c.fee_type === 'fixed'
                        ? `£${parseFloat(c.fixed_fee || 0).toLocaleString()} fixed`
                        : c.fee_type === 'percentage'
                          ? `${c.fee_percentage}% of deal`
                          : c.fee_type || '—'}
                    </td>
                    <td style={{ ...td, textAlign: 'right' }}>{c.total_deals}</td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>{fmtMoney(c.total_allocated)}</td>
                    <td style={{ ...td, textAlign: 'right', color: '#10b981' }}>{fmtMoney(c.total_paid)}</td>
                    <td style={{ ...td, textAlign: 'right', color: parseFloat(c.total_unpaid) > 0 ? '#f59e0b' : '#9ca3af' }}>
                      {fmtMoney(c.total_unpaid)}
                    </td>
                  </tr>
                ))}
              </tbody>
              {filtered.length > 0 && (
                <tfoot>
                  <tr style={{ background: '#f9fafb', fontWeight: 700 }}>
                    <td colSpan={5} style={{ ...td }}>Totals ({filtered.length} introducers)</td>
                    <td style={{ ...td, textAlign: 'right' }}>{fmtMoney(totals.allocated)}</td>
                    <td style={{ ...td, textAlign: 'right', color: '#10b981' }}>{fmtMoney(totals.paid)}</td>
                    <td style={{ ...td, textAlign: 'right', color: '#f59e0b' }}>{fmtMoney(totals.unpaid)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          )}
        </div>
      </main>
    </div>
  );
}

const th = { padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' };
const thBtn = { background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', padding: 0 };
const td = { padding: '12px 16px', fontSize: '14px', color: '#374151', verticalAlign: 'middle' };
const statusDot = { display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', marginRight: '6px' };
const alertRed = { background: '#fee2e2', color: '#991b1b', padding: '12px 16px', borderRadius: '8px', marginBottom: '16px', fontSize: '14px' };
